import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet/es/Helmet'
import { Button, Dropdown, Menu, message, Spin, Tabs, Tooltip } from 'antd'
import { inject, observer } from 'mobx-react'
import DashboardLayout from '../../layouts/DashboardLayout'
import { apiUrl, systemName } from './../../config'
import {
  MoreActionTrigger,
  NotificationAvatar,
  NotificationDate,
  NotificationHeaderRow,
  NotificationListCard,
  NotificationPageContainer,
  NotificationRowItem,
  NotificationRowLeft,
  NotificationRowRight,
  NotificationTextGroup,
  NotificationTitle,
  StyledNotificationMenu,
} from './NotificationPageStyled'
import EmptyContent from '../../components/EmptyContent'
import { CheckOutlined, MoreOutlined } from '@ant-design/icons'
import date_format from '../../date_format'
import InfiniteScroll from 'react-infinite-scroll-component'
import utils from '../../utils'
import { subStringAvatar, WordBreak } from '../../components/Common/CellText'
import { StatusTag } from '../../components/Common/StatusTag'
import { NOTIFICATION_STATUS, TYPE_STATUS } from '../../constants'
import internalMessageStore from '../../stores/internalMessageStore'
import loadingAnimationStore from '../../stores/loadingAnimationStore'

const NotificationPage = props => {
  const { notificationStore, history, commandStore } = props
  const { notificationListTotalCount } = notificationStore

  const [items, setItems] = useState([])
  const [pageIndex, setPageIndex] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [isFilterUnread, setIsFilterUnread] = useState(false)
  const [isLoadingNotification, setIsLoadingNotification] = useState(false)

  const onlyNewsNotificationParams = utils.getParameterByName(
    'only_news_notification'
  )

  /** Xử lý đã đọc thông báo */
  const handleReadNotify = useCallback(
    (id, status, num) => {
      if (status && num === 0) {
        message.warning('Thông báo đã được đọc!')
        return
      }
      if (status) return
      setIsLoadingNotification(true)
      notificationStore
        .readNotification(id)
        .then(() => {
          if (isFilterUnread) {
            const itemsList = items.filter(el => el.id !== id)
            setItems(itemsList)
          } else {
            const itemsList = [...items]
            const indexReadItem = items.findIndex(item => item.id === id)
            if (indexReadItem !== -1) {
              const itemReplace = {
                ...items[indexReadItem],
                status: true,
              }
              itemsList.splice(indexReadItem, 1, itemReplace)
              setItems([...itemsList])
            }
          }
          if (onlyNewsNotificationParams === 'false') {
            notificationStore.setUnreadNotificationCount('remove')
          } else {
            notificationStore.setUnreadNewsCount('remove')
          }
          message.success('Thông báo được đánh dấu đã đọc!')
        })
        .finally(() => setIsLoadingNotification(false))

      commandStore.getSideMenuCounter([
        'WORK',
        'PROPOSAL',
        'INTERNAL_MESSAGE',
        'INCOMING',
        'OUTGOING',
        'ELEC_DOC',
      ])
    },
    [commandStore, isFilterUnread, items, notificationStore, onlyNewsNotificationParams]
  )

  const handleRemoveNotify = () => {
    message.warning('Chức năng đang cập nhật!')
  }

  const menuControlNotification = (id, status) => {
    return (
      <StyledNotificationMenu selectedKeys={[]}>
        <Menu.Item
          key="read"
          className="menu-item-read"
          icon={
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round">
              <path d="M2 13l4.5 4.5L17 7" />
              <path d="M7 13l4.5 4.5L22 7" />
            </svg>
          }
          onClick={({ domEvent }) => {
            domEvent.stopPropagation()
            handleReadNotify(id, status, 0)
          }}>
          Đã đọc
        </Menu.Item>
        <Menu.Item
          key="delete"
          className="menu-item-delete"
          icon={
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round">
              <path d="M3 6h18" />
              <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
              <line x1="10" y1="11" x2="10" y2="17" />
              <line x1="14" y1="11" x2="14" y2="17" />
            </svg>
          }
          onClick={({ domEvent }) => {
            domEvent.stopPropagation()
            handleRemoveNotify()
          }}>
          Xóa
        </Menu.Item>
      </StyledNotificationMenu>
    )
  }

  const goToContentNotification = async (type, id) => {
    if (!type) return

    if (type.slice(0, 5) === 'OTHER') {
      return history.push(`/proposal/other/view/${id}`)
    }

    switch (type) {
      case NOTIFICATION_STATUS.OUTGOING:
        return history.push(`/internal-document/outgoing-document/view/${id}`)
      case NOTIFICATION_STATUS.INCOMING:
      case NOTIFICATION_STATUS.INTERNAL_DOCUMENT_COMMENT:
        return history.push(`/internal-document/incoming-document/view/${id}`)
      case NOTIFICATION_STATUS.VBLT_DI:
        return history.push(`/connected-document/outgoing-document/view/${id}`)
      case NOTIFICATION_STATUS.VBLT_DEN:
        return history.push(`/connected-document/incoming-document/view/${id}`)
      case NOTIFICATION_STATUS.WORK:
        return history.push(`/works/view/${id}`)
      case NOTIFICATION_STATUS.TASK:
        return history.push(`/mission/view/${id}`)
      case NOTIFICATION_STATUS.ELEC_DOC:
        return history.push(`/internal-document/sign-document/view/${id}`)
      case NOTIFICATION_STATUS.VEHICLE:
      case NOTIFICATION_STATUS.LEAVE:
      case NOTIFICATION_STATUS.LEAVE_ANNUAL:
      case NOTIFICATION_STATUS.LEAVE_ABNORMAL:
      case NOTIFICATION_STATUS.LEAVE_NO_SALARY:
      case NOTIFICATION_STATUS.SICK:
      case NOTIFICATION_STATUS.SICK_OTHER:
      case NOTIFICATION_STATUS.OVERTIME_NORMAL:
      case NOTIFICATION_STATUS.OVERTIME_DAYOFF:
      case NOTIFICATION_STATUS.OVERTIME_HOLIDAY:
      case NOTIFICATION_STATUS.OVERTIME_CHAIRMAN:
      case NOTIFICATION_STATUS.BUSINESS_TRIP:
      case NOTIFICATION_STATUS.EVALUATE_MEMBER:
      case NOTIFICATION_STATUS.EVALUATE_LEADER:
      case NOTIFICATION_STATUS.TIMEKEEPER:
      case NOTIFICATION_STATUS.STATIONERY:
      case NOTIFICATION_STATUS.RETIMEKEEPING:
      case NOTIFICATION_STATUS.MEETING:
      case NOTIFICATION_STATUS.PROPOSAL_SALARY:
        return history.push(`/proposal/other/view/${id}`)
      case NOTIFICATION_STATUS.CONSULT:
        return history.push(`/administrative/consult/view/${id}`)
      case NOTIFICATION_STATUS.POLICY:
        return history.push(`/administrative/policy/view/${id}`)
      case NOTIFICATION_STATUS.WORK_SCHEDULE:
        return history.push(`/company-work-schedule/view/${id}`)
      case NOTIFICATION_STATUS.GENERAL:
      case NOTIFICATION_STATUS.NEWS:
        return history.push(`/utility/general-notifications/view/${id}`)
      case NOTIFICATION_STATUS.INTERNAL_MESSAGE_INCOMING:
      case NOTIFICATION_STATUS.INTERNAL_MESSAGE_INCOMING_REPLY: {
        loadingAnimationStore.showSpinner(true)
        const internal_message_user_code =
          await internalMessageStore.getInternalMessageUserCodeByInternalMessageCode(
            id
          )
        loadingAnimationStore.showSpinner(false)
        return history.push(
          `/utility/internal-message/receive/view/${internal_message_user_code}`
        )
      }
      case NOTIFICATION_STATUS.INTERNAL_MESSAGE_OUTGOING:
      case NOTIFICATION_STATUS.INTERNAL_MESSAGE_OUTGOING_REPLY:
        return history.push(`/utility/internal-message/send/view/${id}`)
      default:
        return
    }
  }

  const handleMarkAsRead = async () => {
    setIsLoadingNotification(true)
    try {
      if (onlyNewsNotificationParams === 'false') {
        await notificationStore.markAllAsRead(onlyNewsNotificationParams)
        await notificationStore.getUnreadNotificationCount()
      } else {
        await notificationStore.markAllAsRead(onlyNewsNotificationParams)
        await notificationStore.getUnreadNewsCount()
      }
      const { data } = await notificationStore.getCurrentUserNotification(
        onlyNewsNotificationParams,
        isFilterUnread
      )
      setItems([...(data?.data || [])])
      message.success('Đã đọc tất cả thông báo!')
    } catch (err) {
      console.log(err)
      message.error('Đọc thông báo thất bại!')
    } finally {
      setIsLoadingNotification(false)
    }
  }

  const fetchMoreData = () => {
    if (items.length >= notificationListTotalCount) {
      setHasMore(false)
      return
    }
    notificationStore.setFilter('notificationListPageIndex', pageIndex)
    notificationStore.setFilter('notificationListPageSize', 30)

    setPageIndex(pageIndex + 1)
    notificationStore
      .getCurrentUserNotification(onlyNewsNotificationParams, isFilterUnread)
      .then(res => {
        setItems(prevItems => prevItems.concat([...(res?.data?.data || [])]))
      })
      .catch(err => console.log(err))
  }

  const loadNotifications = useCallback(
    async unreadOnly => {
      notificationStore.setFilter('notificationListPageIndex', 0)
      notificationStore.setFilter('notificationListPageSize', 30)
      setPageIndex(1)
      setHasMore(true)
      setIsLoadingNotification(true)
      try {
        const { data } = await notificationStore.getCurrentUserNotification(
          onlyNewsNotificationParams,
          unreadOnly
        )
        setItems([...(data?.data || [])])
      } catch (error) {
        console.log(error)
        message.error(error?.vi || 'Đã có lỗi xảy ra!')
      } finally {
        setIsLoadingNotification(false)
      }
    },
    [notificationStore, onlyNewsNotificationParams]
  )

  useEffect(() => {
    loadNotifications(false)
    setIsFilterUnread(false)

    return () => {
      notificationStore.clearStore()
      setItems([])
    }
  }, [loadNotifications, notificationStore, onlyNewsNotificationParams])

  const handleFilterTab = key => {
    const unreadOnly = key === 'unread'
    setIsFilterUnread(unreadOnly)
    loadNotifications(unreadOnly)
  }


  return (
    <DashboardLayout title={'Link People - Link the World'}>
      <Helmet>
        <title>Thông báo | {systemName} Portal</title>
      </Helmet>

      <NotificationPageContainer>
        <NotificationHeaderRow>
          <h1 className="page-title">Thông báo</h1>
          <Button
            className="mark-all-btn"
            icon={<CheckOutlined />}
            onClick={handleMarkAsRead}
            loading={isLoadingNotification}>
            Đánh dấu đã đọc
          </Button>
        </NotificationHeaderRow>

        <Tabs
          activeKey={isFilterUnread ? 'unread' : 'all'}
          onChange={handleFilterTab}
          className="notification-tabs">
          <Tabs.TabPane tab="Tất cả" key="all" />
          <Tabs.TabPane tab="Chưa đọc" key="unread" />
        </Tabs>

        <NotificationListCard id={'divScroll'}>
          <InfiniteScroll
            dataLength={items.length}
            next={fetchMoreData}
            hasMore={hasMore}
            loader={
              <div className={'center-spin'}>
                {items.length > 30 ? <Spin tip="Đang tải..." /> : ''}
              </div>
            }
            endMessage={null}
            scrollableTarget={'divScroll'}>
            {isLoadingNotification && items.length === 0 ? (
              <div className="center-spin">
                <Spin tip="Đang tải thông báo..." />
              </div>
            ) : items.length === 0 ? (
              <EmptyContent description={'Không có thông báo!'} />
            ) : (
              items.map(item => {
                return (
                  <NotificationRowItem
                    key={item.id}
                    onClick={() => {
                      handleReadNotify(item.id, item.status, 1)
                      goToContentNotification(item.type, item.code)
                    }}>
                    <NotificationRowLeft>
                      <NotificationAvatar
                        src={
                          item.userImage
                            ? `${apiUrl}/api/v1/images/${item.userImage}`
                            : null
                        }>
                        {subStringAvatar(item.owner)}
                      </NotificationAvatar>

                      <NotificationTextGroup>
                        <NotificationTitle isRead={item.status}>
                          {WordBreak(item.content ? item.content.trim() : '', true)}
                        </NotificationTitle>
                        <Tooltip
                          title={date_format.renderTime(item.time_created)}>
                          <NotificationDate isRead={item.status}>
                            {date_format.renderTime(item.time_created)}
                          </NotificationDate>
                        </Tooltip>
                      </NotificationTextGroup>
                    </NotificationRowLeft>

                    <NotificationRowRight>
                      {StatusTag(TYPE_STATUS.NOTIFICATION, item.type)}
                      <Dropdown
                        overlay={menuControlNotification(item.id, item.status)}
                        trigger={['click']}
                        placement="bottomRight">
                        <MoreActionTrigger
                          onClick={e => {
                            e.stopPropagation()
                          }}>
                          <MoreOutlined />
                        </MoreActionTrigger>
                      </Dropdown>
                    </NotificationRowRight>
                  </NotificationRowItem>
                )
              })
            )}
          </InfiniteScroll>
        </NotificationListCard>
      </NotificationPageContainer>
    </DashboardLayout>
  )
}

NotificationPage.propTypes = {
  notificationStore: PropTypes.object,
  history: PropTypes.object,
  commandStore: PropTypes.object,
}

export default inject('notificationStore', 'commandStore')(observer(NotificationPage))
